/**
 * Path: frontend/src/pages/ChatIAPage.jsx
 */
import { useState, useEffect, useRef } from "react";
import { Send, Trash2, Loader2, Sparkles } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import apiClient from "../Api/apiConfig";

import NonoImage from "../assets/Nono.png";
import "../styles/ChatIAPage.css";

const STORAGE_KEY = "chatHistory_housing_v2";

const API_ORIGIN =
  import.meta.env.VITE_API_ORIGIN ||
  (import.meta.env.VITE_API_URL?.startsWith("http")
    ? new URL(import.meta.env.VITE_API_URL).origin
    : window.location.origin);

function resolveMediaUrl(url) {
  if (!url) return "";
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("data:")
  ) {
    return url;
  }
  const cleanUrl = url.startsWith("/") ? url : `/${url}`;
  return `${API_ORIGIN}${cleanUrl}`;
}

function getDisplayName(user) {
  const fullName = [
    user?.first_name || user?.firstName,
    user?.last_name || user?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || user?.email || "Membre NMS06";
}

export default function ChatIAPage() {
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const chatBodyRef = useRef(null);
  
  const userDisplayName = getDisplayName(user);
  const userAvatar = user?.avatar_url || "";
  const userInitials = userDisplayName.charAt(0).toUpperCase();

  // 🛡️ Accès protégé
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  // 🔄 Initialisation & Accueil
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        setChatHistory(JSON.parse(saved));
      } catch (err) {
        setChatHistory([]);
      }
    } else if (user) {
      // Message d'accueil personnalisé
      const welcome = {
        sender: "ai",
        content: `Bonjour ${userDisplayName.split(' ')[0]}, je suis votre assistant NMS06. Je peux vous aider pour vos démarches administratives, vos questions sur le logement ou la rédaction de courriers. Comment puis-je vous aider aujourd'hui ?`
      };
      setChatHistory([welcome]);
    }
  }, [user, userDisplayName]);

  // 💾 Sauvegarde auto
  useEffect(() => {
    if (chatHistory.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(chatHistory));
    }
  }, [chatHistory]);

  // 📜 Scroll auto
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [chatHistory, loading]);

  const handleSend = async () => {
    const trimmed = message.trim();
    if (!trimmed || loading) return;

    setError("");
    const userMsg = { sender: "user", content: trimmed };
    setChatHistory(prev => [...prev, userMsg]);
    setMessage("");
    setLoading(true);

    try {
      const response = await apiClient.post("/chat", {
        message: trimmed,
        userId: user?.id,
        context: "assistant premium logement et social"
      });

      const aiText = response?.data?.reply || response?.data?.answer || "Désolé, je n'ai pas pu formuler de réponse.";
      
      setChatHistory(prev => [...prev, { sender: "ai", content: aiText }]);
    } catch (err) {
      console.error("Erreur Gemini:", err);
      setError("Impossible de générer la réponse pour le moment.");
      
      setChatHistory(prev => [...prev, { 
        sender: "ai", 
        content: "⚠️ Une erreur technique est survenue. Veuillez vérifier votre connexion ou réessayer plus tard." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    if (window.confirm("Effacer l'historique ?")) {
      setChatHistory([]);
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  return (
    <div className="chatia-page-container">
      <div className="chatia-bg-glow" />

      <div className="chatia-center-column">
        <div className="chatia-card">
          {/* HEADER */}
          <header className="chatia-card-header">
            <div className="chatia-header-avatar">
              <img src={NonoImage} alt="IA" className="chatia-avatar-img" />
              <span className="chatia-status-dot" />
            </div>
            <div className="chatia-header-info">
              <h2>Assistant NMS06</h2>
              <p>Rédaction, aide administrative, réponses rapides</p>
            </div>
            <button className="chatia-clear-btn" onClick={handleClear} title="Vider le chat">
              <Trash2 size={18} />
            </button>
          </header>

          {/* MESSAGES */}
          <div className="chatia-chat-body" ref={chatBodyRef}>
            {chatHistory.map((msg, idx) => (
              <div key={idx} className={`chatia-message ${msg.sender}`}>
                <div className="chatia-msg-avatar">
                  {msg.sender === 'user' ? (
                    userAvatar ? <img src={resolveMediaUrl(userAvatar)} alt="Me" /> : <span>{userInitials}</span>
                  ) : (
                    <img src={NonoImage} alt="Robot" />
                  )}
                </div>
                <div className="chatia-bubble">
                  {msg.content}
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="chatia-message ai">
                <div className="chatia-msg-avatar"><img src={NonoImage} alt="IA" /></div>
                <div className="chatia-bubble">
                  <div className="chatia-typing">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* FOOTER */}
          <footer className="chatia-footer">
            {error && <div className="chatia-error-msg">{error}</div>}
            <div className="chatia-input-wrapper">
              <textarea
                placeholder="Posez votre question ici..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                rows="1"
                disabled={loading}
              />
              <button 
                className="chatia-send-btn" 
                onClick={handleSend} 
                disabled={loading || !message.trim()}
              >
                {loading ? <Loader2 className="spin" size={20} /> : <Send size={20} />}
              </button>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
